import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { animate, motion, useMotionValue } from 'motion/react';
import LifebuoyIcon from '@/assets/icons/lifebuoy.svg?react';
import CrossIcon from '@/assets/icons/cross.svg?react';
import { ClueStep1 } from './ClueStep1';
import { ClueStep2 } from './ClueStep2';
import { ClueStep3 } from './ClueStep3';
import clsx from 'clsx';

interface Props {
  currentStep: number;
  stepIsChanging: boolean;
}

export const CluesOverlay = ({ currentStep, stepIsChanging }: Props) => {
  const [open, setOpen] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  const isMobile = window.innerWidth < 768;
  const x = useMotionValue(isMobile ? 48 : 64);
  const y = useMotionValue(isMobile ? 120 : 112);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wasDragged = useRef(false);
  const [clipOrigin, setClipOrigin] = useState('50% 0px');
  const [dragConstraints, setDragConstraints] = useState({
    left: 0,
    top: 0,
    right: window.innerWidth - 150,
    bottom: window.innerHeight - 60,
  });

  useLayoutEffect(() => {
    if (!buttonRef.current) return;

    const { width, height } = buttonRef.current.getBoundingClientRect();
    setClipOrigin(`50% ${64 + height / 2}px`);
    setDragConstraints({
      left: 16,
      top: 16,
      right: window.innerWidth - 16 - width,
      bottom: window.innerHeight - 16 - height,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setOverlayOpen(false); // eslint-disable-line react-hooks/set-state-in-effect
      return;
    }

    let cancelled = false;

    const buttonWidth = buttonRef.current?.offsetWidth ?? 120;
    void Promise.all([
      animate(x, window.innerWidth / 2 - buttonWidth / 2, { type: 'spring', stiffness: 380, damping: 30 }),
      animate(y, 64, { type: 'spring', stiffness: 380, damping: 30 }),
    ]).then(() => {
      if (cancelled) return;

      setOverlayOpen(true);
    });

    return () => {
      cancelled = true;
    };
  }, [open, x, y]);

  return (
    <>
      <motion.button
        ref={buttonRef}
        drag={!open}
        initial={{ opacity: 0 }}
        animate={{ opacity: stepIsChanging ? 0 : 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        transition={{
          scale: { type: 'tween', duration: 0.15, ease: 'easeInOut' },
          opacity: { duration: stepIsChanging ? 0.25 : 0.4, delay: stepIsChanging ? 0.1 : 0.9 },
        }}
        dragTransition={{ power: 0.2, timeConstant: 220, bounceStiffness: 300, bounceDamping: 30 }}
        dragConstraints={dragConstraints}
        onDragStart={() => {
          wasDragged.current = true;
        }}
        onTap={() => {
          if (wasDragged.current) {
            wasDragged.current = false;
            return;
          }
          setOpen(prev => !prev);
        }}
        style={{ position: 'fixed', top: 0, left: 0, x, y }}
        className={clsx(
          'z-50 select-none px-4 py-3 cursor-pointer border-2 rounded-full border-solid flex items-center gap-2 md:gap-3 md:text-xl will-change-transform',
          'drop-shadow-black/50 drop-shadow-[0_0_20px]',
          open && 'bg-black text-white',
          !open && currentStep === 1 && 'bg-beige text-dark-beige',
          !open && currentStep === 2 && 'bg-green text-light-green',
          !open && currentStep === 3 && 'bg-red text-black',
        )}
      >
        {open ? <CrossIcon className="h-2 md:h-3 w-auto" /> : <LifebuoyIcon className="h-4 md:h-5 w-auto" />}
        {open ? 'Fermer' : 'Indices'}
      </motion.button>

      <motion.div
        initial={{ clipPath: `circle(0% at ${clipOrigin})` }}
        animate={{ clipPath: overlayOpen ? `circle(150% at ${clipOrigin})` : `circle(0% at ${clipOrigin})` }}
        transition={overlayOpen ? { duration: 0.55, ease: [0.35, 0, 0.1, 1] } : { duration: 0.4, ease: [0.4, 0, 1, 1] }}
        className={clsx(
          'fixed inset-0 z-49 bg-black text-white overflow-y-auto p-4 pt-40 pb-20 md:p-16 md:pt-32 flex flex-col gap-16 items-center justify-evenly will-change-[clip-path]',
          overlayOpen ? 'pointer-events-auto' : 'pointer-events-none',
        )}
      >
        <span className="text-2xl md:text-4xl italic">Zone d’indices</span>

        <div className="flex flex-col md:flex-row items-center justify-around gap-32 w-full">
          <ClueStep3 />
          <ClueStep1 />
          <ClueStep2 />
        </div>
      </motion.div>
    </>
  );
};
